import React, { useMemo } from 'react';
import { ICON_STROKE } from '../../constants/icon-stroke';
import {
  bookingBrandFontFamilyVar,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import type { BookingBrandColors } from '../../utils/booking-brand-colors';
import { buildBookingSurfaceStyles } from '../../utils/booking-brand-colors';
import { ShopSectionHeader, shopCardStyle } from './ShopCatalogCards';
import type { CheckoutIntentResponse } from '../../services/payments';
import type {
  ShopCartLine,
  ShopGiftCardValidation,
  ShopPublicPaymentMethod,
  ShopQuote,
} from '../../services/shop';
import type { BookingCustomer } from '../../services/customer-auth.service';
import { formatMoneyFromCents } from '../../utils/format-money';
import { shopGiftCardCartLabel } from '../../utils/shop-gift-card-line';
import { BookingsIcon } from '../Icons/BookingsIcon';
import OnlineBookingPaymentStep from '../OnlineBooking/OnlineBookingPaymentStep';
import ShopDevPaymentStep from './ShopDevPaymentStep';
import ShopPaymentMethodSelector from './ShopPaymentMethodSelector';
import {
  filterShopCheckoutPaymentMethods,
  filterShopDisplayPaymentMethods,
  getShopPaymentMethodsEmptyMessage,
  isShopPaymentMethodSelectable,
} from '../../utils/shop-checkout-payment-methods';

type Surface = ReturnType<typeof buildBookingSurfaceStyles>;

type Props = {
  isMobile: boolean;
  brandColors: BookingBrandColors;
  surface: Surface;
  clubName?: string;
  paymentsReady?: boolean;
  devPaymentBypassAvailable?: boolean;
  paymentMethods: ShopPublicPaymentMethod[];
  selectedPaymentMethodCode?: string;
  onSelectPaymentMethod: (code: string) => void;
  walletBackedPaymentMethodsAllowed: boolean;
  walletBalanceLabel?: string | null;
  walletLoading?: boolean;
  cartHasGiftCardPurchase: boolean;
  giftCardCode: string;
  onGiftCardCodeChange: (code: string) => void;
  giftCardValidation: ShopGiftCardValidation | null;
  giftCardValidating: boolean;
  onValidateGiftCard: () => void;
  secondaryPaymentMethodCode?: string;
  onSelectSecondaryPaymentMethod: (code: string) => void;
  cart: ShopCartLine[];
  quote: ShopQuote | null;
  quoteLoading: boolean;
  authenticatedCustomer: BookingCustomer | null;
  checkoutIntent: CheckoutIntentResponse | null;
  checkoutChargeCents: number | null;
  checkoutError: string | null;
  busy: boolean;
  onRemoveLine: (index: number) => void;
  onSignIn: () => void;
  onContinueToCardPayment: () => void;
  onPayWithWallet: () => void;
  onPayWithGiftCard: () => void;
  onPaymentSuccess: (result: { paymentIntentId?: string }) => void;
  onPaymentError: (message: string) => void;
  passLabel?: (passProductId: string) => string | undefined;
};

export function ShopCheckoutPanel({
  isMobile,
  brandColors,
  surface,
  clubName,
  paymentsReady = true,
  devPaymentBypassAvailable = false,
  paymentMethods,
  selectedPaymentMethodCode,
  onSelectPaymentMethod,
  walletBackedPaymentMethodsAllowed,
  walletBalanceLabel,
  walletLoading = false,
  cartHasGiftCardPurchase,
  giftCardCode,
  onGiftCardCodeChange,
  giftCardValidation,
  giftCardValidating,
  onValidateGiftCard,
  secondaryPaymentMethodCode,
  onSelectSecondaryPaymentMethod,
  cart,
  quote,
  quoteLoading,
  authenticatedCustomer,
  checkoutIntent,
  checkoutChargeCents,
  checkoutError,
  busy,
  onRemoveLine,
  onSignIn,
  onContinueToCardPayment,
  onPayWithWallet,
  onPayWithGiftCard,
  onPaymentSuccess,
  onPaymentError,
  passLabel,
}: Props) {
  const tebox = useBookingTebox();
  const sp = teboxDesignSystem.spacing;

  const checkoutPaymentOptions = useMemo(
    () => ({
      cartHasGiftCardPurchase,
      walletBackedPaymentMethodsAllowed,
      paymentsReady,
      devPaymentBypassAvailable,
    }),
    [
      cartHasGiftCardPurchase,
      walletBackedPaymentMethodsAllowed,
      paymentsReady,
      devPaymentBypassAvailable,
    ],
  );

  const displayPaymentMethods = useMemo(
    () => filterShopDisplayPaymentMethods(paymentMethods, checkoutPaymentOptions),
    [paymentMethods, checkoutPaymentOptions],
  );

  const selectablePaymentMethods = useMemo(
    () => filterShopCheckoutPaymentMethods(paymentMethods, checkoutPaymentOptions),
    [paymentMethods, checkoutPaymentOptions],
  );

  const selectablePaymentCodes = useMemo(
    () => new Set(selectablePaymentMethods.map((method) => method.code)),
    [selectablePaymentMethods],
  );

  const emptyPaymentMessage = useMemo(
    () =>
      getShopPaymentMethodsEmptyMessage({
        catalogMethodCount: paymentMethods.length,
        cartHasGiftCardPurchase,
        paymentsReady,
        devPaymentBypassAvailable,
      }),
    [paymentMethods.length, cartHasGiftCardPurchase, paymentsReady, devPaymentBypassAvailable],
  );

  const visiblePaymentMethods = displayPaymentMethods;

  const selectedMethod =
    visiblePaymentMethods.find((m) => m.code === selectedPaymentMethodCode) ??
    visiblePaymentMethods[0];

  const selectedMethodSelectable = selectedMethod
    ? isShopPaymentMethodSelectable(selectedMethod, checkoutPaymentOptions)
    : false;

  const secondaryMethods = selectablePaymentMethods.filter((m) => m.channel !== 'gift_card');
  const cardChargeCents = checkoutChargeCents ?? quote?.totalCents ?? 0;
  const canProceed = Boolean(quote && !quoteLoading && !busy);

  const lineSecondaryStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: isMobile ? 13 : 14,
    color: tebox.colors.textSecondary,
  };

  const lineAmountStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile ? 13 : 14,
    color: '#000000',
  };

  const primaryButtonStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile ? 17 : 16,
    backgroundColor: brandColors.buttonColor,
    color: brandColors.primaryTextColor,
    minHeight: isMobile ? 52 : undefined,
    border: 'none',
    cursor: busy || quoteLoading || !quote ? 'wait' : 'pointer',
  };

  const lineLabel = (line: ShopCartLine) => {
    if (line.type === 'gift_card') {
      return shopGiftCardCartLabel(line);
    }
    const fromQuote = quote?.lines.find(
      (l) => l.type === 'pass' && l.passProductId === line.passProductId,
    );
    return fromQuote?.label ?? passLabel?.(line.passProductId) ?? 'Pass';
  };

  const quoteLineForCartLine = (line: ShopCartLine, index: number) => {
    if (!quote) return undefined;
    if (line.type === 'pass') {
      return quote.lines.find(
        (l) => l.type === 'pass' && l.passProductId === line.passProductId,
      );
    }
    const giftLines = quote.lines.filter((l) => l.type === 'gift_card');
    const giftOrdinal = cart.slice(0, index + 1).filter((l) => l.type === 'gift_card').length - 1;
    return giftLines[giftOrdinal];
  };

  const renderPayButton = (label: string, onClick: () => void, disabled?: boolean) => (
    <button
      type="button"
      disabled={disabled || !canProceed}
      onClick={onClick}
      className={`w-full ${isMobile ? 'py-4 px-6 text-tebox-h4' : 'py-4 px-6 text-tebox-body'} rounded-[6px] font-medium transition-colors disabled:opacity-50`}
      style={primaryButtonStyle}
    >
      {busy ? 'Processing…' : label}
    </button>
  );

  return (
    <div
      className={`min-w-0 bg-white rounded-[6px] overflow-hidden ${isMobile ? 'w-full' : 'shrink-0'}`}
      style={{
        width: isMobile ? '100%' : 576,
        boxSizing: 'border-box',
        ...(isMobile
          ? { overflow: 'visible' }
          : {
              position: 'sticky' as const,
              top: 24,
              maxHeight: 'calc(100vh - 160px)',
              overflowY: 'auto' as const,
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }),
      }}
    >
      <div
        style={{ padding: isMobile ? 16 : 24, boxSizing: 'border-box' }}
        className={isMobile ? 'space-y-4' : 'space-y-6'}
      >
        <section>
          <ShopSectionHeader
            icon={
              <BookingsIcon
                size={22}
                color={brandColors.textColor}
                strokeWidth={ICON_STROKE.standard}
              />
            }
            title="Order Summary"
            surface={surface}
          />

          <div
            className={isMobile ? 'space-y-2' : 'space-y-3'}
            style={{
              ...shopCardStyle(surface, brandColors),
              padding: sp.md,
              boxSizing: 'border-box',
            }}
          >
            {cart.length > 0 ? (
              <div className="min-w-0 text-left">
                <p
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: teboxDesignSystem.typography.caption.fontSize,
                    lineHeight: 1.35,
                    color: tebox.colors.textSecondary,
                    margin: 0,
                  }}
                >
                  {cart.length} {cart.length === 1 ? 'item' : 'items'}
                </p>
                {clubName ? (
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                      lineHeight: 1.35,
                      color: brandColors.textColor,
                      margin: '2px 0 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={clubName}
                  >
                    {clubName}
                  </p>
                ) : null}
              </div>
            ) : null}

            {cart.length === 0 ? (
              <p
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  color: tebox.colors.textSecondary,
                  margin: 0,
                }}
              >
                Your cart is empty. Add a gift card or pass to checkout.
              </p>
            ) : (
              <>
                {cart.map((line, index) => {
                  const quoteLine = quoteLineForCartLine(line, index);
                  return (
                    <div key={`${line.type}-${index}`}>
                      <div className="flex justify-between text-tebox-body">
                        <span style={lineSecondaryStyle}>{lineLabel(line)}</span>
                        <span style={lineAmountStyle}>
                          {quoteLine
                            ? formatMoneyFromCents(quoteLine.amountCents)
                            : quoteLoading
                              ? '…'
                              : '—'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveLine(index)}
                        style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontSize: teboxDesignSystem.typography.caption.fontSize,
                          color: tebox.colors.textSecondary,
                          background: 'none',
                          border: 'none',
                          padding: '2px 0 0',
                          margin: 0,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}

                {quote ? (
                  <>
                    <div className="pt-3">
                      <div className="flex justify-between text-tebox-body font-medium">
                        <span
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: isMobile ? 15 : 16,
                            color: '#000000',
                          }}
                        >
                          Sub-total:
                        </span>
                        <span
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: isMobile ? 15 : 16,
                            color: '#000000',
                          }}
                        >
                          {formatMoneyFromCents(quote.subtotalCents)}
                        </span>
                      </div>
                    </div>

                    {quote.taxCents > 0 ? (
                      <div className="flex justify-between text-tebox-body">
                        <span style={lineSecondaryStyle}>Tax:</span>
                        <span style={lineAmountStyle}>
                          {formatMoneyFromCents(quote.taxCents)}
                        </span>
                      </div>
                    ) : null}

                    <div className="pt-3">
                      <div className="flex justify-between text-tebox-h4 font-medium">
                        <span
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: isMobile ? 18 : 20,
                            color: brandColors.textColor,
                          }}
                        >
                          Total:
                        </span>
                        <span
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: isMobile ? 18 : 20,
                            color: brandColors.textColor,
                          }}
                        >
                          {formatMoneyFromCents(quote.totalCents)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : quoteLoading ? (
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                      color: tebox.colors.textSecondary,
                      margin: 0,
                    }}
                  >
                    Updating totals…
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>

        {cart.length > 0 && !checkoutIntent ? (
          !authenticatedCustomer ? (
            <div className="space-y-3">
              <div
                className={`text-center ${isMobile ? 'p-3' : 'p-4'} bg-amber-50 border border-amber-200 rounded-[6px]`}
              >
                <div
                  className={`text-amber-800 font-medium ${isMobile ? 'mb-2 text-tebox-body' : 'mb-2'}`}
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: isMobile ? 15 : 14,
                  }}
                >
                  Login Required
                </div>
                <div
                  className={`${isMobile ? 'text-tebox-body mb-3' : 'text-tebox-body mb-3'} text-amber-700`}
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: isMobile ? 13 : 14,
                  }}
                >
                  You must be logged in to complete your purchase
                </div>
                <button
                  type="button"
                  onClick={onSignIn}
                  className={`w-full ${isMobile ? 'py-3.5 px-6 text-tebox-body' : 'py-3 px-6 text-tebox-body'} bg-amber-600 text-white rounded-[6px] font-medium hover:bg-amber-700 transition-colors`}
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontSize: isMobile ? 15 : 14,
                    minHeight: isMobile ? 44 : undefined,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Log In to Continue
                </button>
              </div>
            </div>
          ) : displayPaymentMethods.length === 0 ? (
            <div
              className={`${isMobile ? 'p-3' : 'p-4'} bg-red-50 border border-red-200 rounded-[6px]`}
            >
              <div
                className="font-medium text-red-800"
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontSize: isMobile ? 15 : 14,
                  marginBottom: 8,
                }}
              >
                {emptyPaymentMessage.title}
              </div>
              <p
                className="text-red-700"
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontSize: isMobile ? 13 : 14,
                  margin: 0,
                }}
              >
                {emptyPaymentMessage.body}
              </p>
            </div>
          ) : (
            <div className={`${isMobile ? 'space-y-3' : 'space-y-4'} pt-2`}>
              {!paymentsReady &&
              !devPaymentBypassAvailable &&
              visiblePaymentMethods.every((method) => method.channel !== 'card') ? (
                <div
                  className={`${isMobile ? 'p-3' : 'p-4'} bg-amber-50 border border-amber-200 rounded-[6px]`}
                >
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontSize: isMobile ? 13 : 14,
                      color: '#92400e',
                      margin: 0,
                    }}
                  >
                    Card payments are not set up for {clubName ?? 'this club'} yet. The club needs
                    to finish online payment setup (Stripe Connect) before cards can be accepted.
                    You can pay with a gift card if you have one.
                  </p>
                </div>
              ) : null}
              <div>
                <p
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontWeight: 500,
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    color: brandColors.textColor,
                    margin: '0 0 8px 0',
                  }}
                >
                  Payment method
                </p>
                <ShopPaymentMethodSelector
                  methods={visiblePaymentMethods}
                  selectedCode={selectedMethod?.code}
                  onSelect={onSelectPaymentMethod}
                  brandColors={brandColors}
                  disabled={busy}
                  selectableCodes={selectablePaymentCodes}
                  emptyMessage={emptyPaymentMessage.body}
                />
              </div>

              {selectedMethod?.channel === 'wallet' ? (
                <div className="space-y-3">
                  {walletLoading ? (
                    <p style={{ ...lineSecondaryStyle, margin: 0 }}>Loading wallet…</p>
                  ) : walletBackedPaymentMethodsAllowed && walletBalanceLabel ? (
                    <p style={{ ...lineSecondaryStyle, margin: 0 }}>{walletBalanceLabel}</p>
                  ) : (
                    <p style={{ ...lineSecondaryStyle, margin: 0, color: '#b45309' }}>
                      A club wallet is required to pay with wallet. Contact the club if you believe
                      this is an error.
                    </p>
                  )}
                  {renderPayButton(
                    'Pay with wallet',
                    onPayWithWallet,
                    !selectedMethodSelectable || !walletBackedPaymentMethodsAllowed,
                  )}
                </div>
              ) : null}

              {selectedMethod?.channel === 'gift_card' ? (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="shop-gift-card-code"
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        fontWeight: 500,
                        color: brandColors.textColor,
                        display: 'block',
                        marginBottom: 6,
                      }}
                    >
                      Gift card code
                    </label>
                    <input
                      id="shop-gift-card-code"
                      type="text"
                      value={giftCardCode}
                      onChange={(e) => onGiftCardCodeChange(e.target.value)}
                      placeholder="Enter gift card code"
                      disabled={busy}
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        borderRadius: brandColors.borderRadius,
                        border: `1px solid ${brandColors.borderColor}`,
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={busy || !giftCardCode.trim() || giftCardValidating}
                    onClick={onValidateGiftCard}
                    className="w-full py-2.5 px-4 rounded-[6px] font-medium transition-colors disabled:opacity-50"
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      border: `1px solid ${brandColors.borderColor}`,
                      backgroundColor: '#FFFFFF',
                      color: brandColors.textColor,
                      cursor: busy ? 'wait' : 'pointer',
                    }}
                  >
                    {giftCardValidating ? 'Checking…' : 'Apply gift card'}
                  </button>
                  {giftCardValidation?.valid ? (
                    <div
                      className="rounded-[6px] p-3"
                      style={{
                        backgroundColor: 'color-mix(in srgb, #16a34a 8%, #FFFFFF)',
                        border: '1px solid #bbf7d0',
                      }}
                    >
                      <p style={{ ...lineSecondaryStyle, margin: 0, color: '#166534' }}>
                        {giftCardValidation.codeMasked
                          ? `Card ${giftCardValidation.codeMasked}`
                          : 'Gift card applied'}
                        {' — '}
                        {formatMoneyFromCents(giftCardValidation.redeemAmountCents)} applied
                        {giftCardValidation.remainderCents > 0
                          ? `, ${formatMoneyFromCents(giftCardValidation.remainderCents)} remaining`
                          : ''}
                      </p>
                    </div>
                  ) : null}
                  {giftCardValidation?.requiresSecondaryPayment ? (
                    <div>
                      <p
                        style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontWeight: 500,
                          fontSize: teboxDesignSystem.typography.body.fontSize,
                          color: brandColors.textColor,
                          margin: '0 0 8px 0',
                        }}
                      >
                        Pay remaining balance with
                      </p>
                      <ShopPaymentMethodSelector
                        methods={secondaryMethods}
                        selectedCode={secondaryPaymentMethodCode}
                        onSelect={onSelectSecondaryPaymentMethod}
                        brandColors={brandColors}
                        disabled={busy}
                      />
                    </div>
                  ) : null}
                  {renderPayButton(
                    giftCardValidation?.requiresSecondaryPayment ? 'Continue to payment' : 'Pay with gift card',
                    onPayWithGiftCard,
                    !giftCardValidation?.valid || (giftCardValidation.requiresSecondaryPayment && !secondaryPaymentMethodCode),
                  )}
                </div>
              ) : null}

              {selectedMethod?.channel === 'card' ? (
                <div className="pt-1">
                  {renderPayButton(
                    'Continue to payment',
                    onContinueToCardPayment,
                    !selectedMethodSelectable,
                  )}
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {checkoutIntent && quote ? (
          <div className={`${isMobile ? 'space-y-2' : 'space-y-3'} border border-tebox-border rounded-[6px] p-4`}>
            <label
              className={`block ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} font-medium text-tebox-ink`}
              style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontSize: isMobile ? 14 : 14,
              }}
            >
              {checkoutIntent.devBypass ? 'Test payment' : 'Payment required'}
              {cardChargeCents !== quote.totalCents ? (
                <span style={{ fontWeight: 400, marginLeft: 8 }}>
                  ({formatMoneyFromCents(cardChargeCents)})
                </span>
              ) : null}
            </label>
            {checkoutIntent.devBypass ? (
              <ShopDevPaymentStep
                amountCents={cardChargeCents}
                intentId={checkoutIntent.intentId}
                onSuccess={({ paymentIntentId }) => onPaymentSuccess({ paymentIntentId })}
                onError={onPaymentError}
                disabled={busy}
                brandColors={{
                  textColor: brandColors.textColor,
                  buttonColor: brandColors.buttonColor,
                  primaryTextColor: brandColors.primaryTextColor,
                  borderRadius: brandColors.borderRadius,
                  borderColor: brandColors.borderColor,
                  backgroundColor: brandColors.backgroundColor,
                }}
              />
            ) : (
              <OnlineBookingPaymentStep
                intent={checkoutIntent}
                amountCents={cardChargeCents}
                onSuccess={onPaymentSuccess}
                onError={onPaymentError}
                disabled={busy}
                brandColors={{
                  textColor: brandColors.textColor,
                  buttonColor: brandColors.buttonColor,
                  primaryTextColor: brandColors.primaryTextColor,
                  borderRadius: brandColors.borderRadius,
                }}
              />
            )}
          </div>
        ) : null}

        {checkoutError ? (
          <div
            className={`mt-3 ${isMobile ? 'p-2.5' : 'p-3'} bg-red-50 border border-red-200 rounded ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-red-700`}
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: isMobile ? 13 : 14,
            }}
          >
            {checkoutError}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ShopCheckoutPanel;
